import { ArrowRight, BadgeDollarSign, Calculator, Check, ChevronDown, ExternalLink, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fallbackCatalog } from "./fallbackCatalog";
import { RankingsPage } from "./RankingsPage";
import { rankingsCopy } from "../shared/rankings";
import { CALCULATOR_PATH, COMPARE_PATH, GUIDE_PATH, localizedPath, modelPath, providerPath, type AppRoute } from "../shared/routes";
import {
  CURATED_COMPARISONS,
  compareDirectoryCopy,
  findCuratedComparison,
  resolveCuratedModels,
} from "../shared/curatedComparisons";
import {
  REQUEST_COST_BOUNDS,
  clampRequestCostField,
  estimateRequestCost,
  requestCostCopy,
  requestCostInputFromSearch,
  requestCostSearchParams,
  resolveCalculatorModel,
  type RequestCostField,
  type RequestCostInput,
} from "../shared/requestCostCalculator";
import type { Language, TranslationKey } from "./i18n";
import { SharePanel } from "./share";
import type { CatalogResponse, PriceModel } from "./types";
import { formatMoney, formatTokens, monthlyCost, sortModels } from "./utils";

type Translator = (key: TranslationKey, values?: Record<string, string | number>) => string;

const EXAMPLE_INPUT_MILLIONS = 10;
const EXAMPLE_OUTPUT_MILLIONS = 2;
const EXAMPLE_CACHE_PERCENT = 20;

export type UsageState = {
  inputMillions: number;
  outputMillions: number;
  cachePercent: number;
  setInputMillions: (value: number) => void;
  setOutputMillions: (value: number) => void;
  setCachePercent: (value: number) => void;
};

type PageProps = {
  route: AppRoute;
  catalog: CatalogResponse | null;
  loading: boolean;
  language: Language;
  locale: string;
  t: Translator;
  usage: UsageState;
};

function href(path: string, language: Language, values?: Record<string, string | number | undefined>) {
  return localizedPath(path, language, values);
}

function absoluteUrl(path: string) {
  return new URL(path, "https://agentbuck.com").toString();
}

function PageLogo({ model, size = "lg" }: { model: PriceModel; size?: "md" | "lg" }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`provider-logo provider-logo--${size}`} aria-hidden="true">
      {failed ? model.providerName.slice(0, 1) : <img src={model.logo} alt="" onError={() => setFailed(true)} />}
    </span>
  );
}

function Breadcrumbs({ items, language, t }: { items: Array<[string, string?]>; language: Language; t: Translator }) {
  return (
    <nav className="breadcrumbs" aria-label={t("aria.breadcrumb")}>
      <a href={href("/", language)}>{t("common.home")}</a>
      {items.map(([label, path], index) => (
        <span key={`${label}-${index}`}>
          <i>/</i>
          {path ? <a href={href(path, language)}>{label}</a> : <strong>{label}</strong>}
        </span>
      ))}
    </nav>
  );
}

function CapabilityList({ model, t }: { model: PriceModel; t: Translator }) {
  const items: Array<[boolean, TranslationKey]> = [
    [model.capabilities.reasoning, "cap.reasoning"],
    [model.capabilities.tools, "cap.tools"],
    [model.capabilities.vision, "cap.vision"],
    [model.capabilities.structured, "cap.structured"],
    [model.capabilities.openWeights, "cap.openWeights"],
  ];
  return (
    <div className="detail-capabilities">
      {items.filter(([active]) => active).map(([, key]) => <span key={key}><Check size={12} /> {t(key)}</span>)}
    </div>
  );
}

function CompactUsage({ usage, t }: { usage: UsageState; t: Translator }) {
  const setNumber = (setter: (value: number) => void) => (value: string) => {
    const parsed = Number(value);
    setter(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0);
  };
  return (
    <div className="compact-usage">
      <label>
        <span>{t("calculator.input")}</span>
        <div><input type="number" min="0" value={usage.inputMillions} onChange={(event) => setNumber(usage.setInputMillions)(event.target.value)} /><small>M</small></div>
      </label>
      <label>
        <span>{t("calculator.output")}</span>
        <div><input type="number" min="0" value={usage.outputMillions} onChange={(event) => setNumber(usage.setOutputMillions)(event.target.value)} /><small>M</small></div>
      </label>
      <label className="compact-cache">
        <span>{t("calculator.cache")} <strong>{usage.cachePercent}%</strong></span>
        <input type="range" min="0" max="100" step="5" value={usage.cachePercent} onChange={(event) => usage.setCachePercent(Number(event.target.value))} />
      </label>
    </div>
  );
}

function ModelCard({ model, language, locale, t }: { model: PriceModel; language: Language; locale: string; t: Translator }) {
  return (
    <a className="directory-model-card" href={href(modelPath(model.providerId, model.slug), language)}>
      <div>
        <PageLogo model={model} size="md" />
        <span>{model.providerName}</span>
      </div>
      <strong>{model.name}</strong>
      <p>{language === "en" ? model.description : t("model.descriptionFallback")}</p>
      <dl>
        <div><dt>{t("column.input")}</dt><dd>{formatMoney(model.cost.input, locale)}</dd></div>
        <div><dt>{t("column.output")}</dt><dd>{formatMoney(model.cost.output, locale)}</dd></div>
        <div><dt>{t("column.context")}</dt><dd>{formatTokens(model.context)}</dd></div>
      </dl>
    </a>
  );
}

function ProvidersPage({ catalog, language, locale, t }: Omit<PageProps, "route" | "loading" | "usage">) {
  const models = catalog?.models ?? [];
  return (
    <main className="subpage">
      <Breadcrumbs items={[[t("common.providers")]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy">
          <span>{t("providers.kicker")}</span>
          <h1>{t("providers.title")}</h1>
          <p>{t("providers.intro")}</p>
        </div>
        <SharePanel title={t("providers.title")} text={t("providers.intro")} url={absoluteUrl(href(providerPath(), language))} itemId="providers" t={t} />
      </header>
      <section className="provider-directory" aria-label={t("providers.title")}>
        {catalog?.providers.map((provider) => {
          const providerModels = models.filter((model) => model.providerId === provider.id);
          const first = providerModels[0];
          const minimum = Math.min(...providerModels.map((model) => model.cost.input));
          return (
            <a className="provider-directory-card" href={href(providerPath(provider.id), language)} key={provider.id}>
              <div className="provider-card-head">
                {first ? <PageLogo model={first} size="lg" /> : <BadgeDollarSign size={22} />}
                <ArrowRight size={17} />
              </div>
              <h2>{provider.name}</h2>
              <p>{t("providers.models", { count: provider.count })}</p>
              <dl><dt>{t("providers.starting")}</dt><dd>{formatMoney(minimum, locale)}</dd></dl>
            </a>
          );
        })}
      </section>
    </main>
  );
}

function ProviderPage({ route, catalog, language, locale, t }: Omit<PageProps, "loading" | "usage"> & { route: Extract<AppRoute, { type: "provider" }> }) {
  const provider = catalog?.providers.find((item) => item.id === route.providerId);
  const models = (catalog?.models ?? []).filter((model) => model.providerId === route.providerId);
  if (!provider || !models.length) return <NotFoundPage language={language} t={t} />;
  const sorted = sortModels(models, "input", 10, 2, 0);
  const largestContext = Math.max(...models.map((model) => model.context));
  const description = t("provider.description", { count: models.length, name: provider.name });
  const pageTitle = `${provider.name} ${t("provider.titleSuffix")}`;
  return (
    <main className="subpage">
      <Breadcrumbs items={[[t("common.providers"), providerPath()], [provider.name]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy page-hero-with-logo">
          <PageLogo model={models[0]} size="lg" />
          <div><span>{t("provider.kicker")}</span><h1>{pageTitle}</h1><p>{description}</p></div>
        </div>
        <SharePanel title={pageTitle} text={description} url={absoluteUrl(href(providerPath(provider.id), language))} itemId={`provider:${provider.id}`} t={t} />
      </header>
      <dl className="page-stats">
        <div><dt>{t("common.models")}</dt><dd>{models.length}</dd></div>
        <div><dt>{t("provider.cheapest")}</dt><dd>{formatMoney(sorted[0].cost.input, locale)}</dd></div>
        <div><dt>{t("provider.largestContext")}</dt><dd>{formatTokens(largestContext)}</dd></div>
        <div><dt>{t("provider.updated")}</dt><dd>{catalog?.updatedAt.slice(0, 10)}</dd></div>
      </dl>
      <section className="directory-model-grid">
        {sorted.map((model) => <ModelCard key={model.uid} model={model} language={language} locale={locale} t={t} />)}
      </section>
    </main>
  );
}

function ModelPage({ route, catalog, language, locale, t, usage }: Omit<PageProps, "loading"> & { route: Extract<AppRoute, { type: "model" }> }) {
  const model = catalog?.models.find((item) => item.providerId === route.providerId && item.slug === route.slug);
  if (!model) return <NotFoundPage language={language} t={t} />;
  const description = language === "en" && model.description ? model.description : t("model.descriptionFallback");
  const modelHref = href(modelPath(model.providerId, model.slug), language, {
    input: usage.inputMillions,
    output: usage.outputMillions,
    cache: usage.cachePercent,
  });
  const related = (catalog?.models ?? [])
    .filter((item) => item.providerId === model.providerId && item.uid !== model.uid)
    .sort((a, b) => (b.releaseDate ?? "").localeCompare(a.releaseDate ?? ""))
    .slice(0, 6);
  const directoryCopy = compareDirectoryCopy(language);
  const featuredComparisons = CURATED_COMPARISONS.map((definition, index) => ({
    slug: definition.slug,
    title: directoryCopy.comparisons[index].title,
    // A model uid can be listed on a curated comparison while that comparison is degraded
    // below the 2-model availability floor; don't link to a page that renders unavailable
    // and is served noindex/excluded from the sitemap.
    included:
      definition.modelUids.includes(model.uid) &&
      resolveCuratedModels(definition, catalog?.models ?? fallbackCatalog.models).isAvailable,
  })).filter((item) => item.included);
  const exampleTotal = monthlyCost(model, EXAMPLE_INPUT_MILLIONS, EXAMPLE_OUTPUT_MILLIONS, EXAMPLE_CACHE_PERCENT);
  return (
    <main className="subpage">
      <Breadcrumbs items={[[t("common.providers"), providerPath()], [model.providerName, providerPath(model.providerId)], [model.name]]} language={language} t={t} />
      <header className="page-hero model-page-hero">
        <div className="page-hero-copy page-hero-with-logo">
          <PageLogo model={model} size="lg" />
          <div><span>{t("model.kicker")}</span><h1>{t("model.title", { name: model.name })}</h1><p>{description}</p></div>
        </div>
        <SharePanel title={t("model.title", { name: model.name })} text={description} url={absoluteUrl(modelHref)} itemId={model.uid} t={t} />
      </header>
      <section className="detail-layout">
        <div>
          <dl className="detail-prices">
            <div><dt>{t("model.input")}</dt><dd>{formatMoney(model.cost.input, locale)}</dd></div>
            <div><dt>{t("model.cache")}</dt><dd>{model.cost.cacheRead === undefined ? "-" : formatMoney(model.cost.cacheRead, locale)}</dd></div>
            <div><dt>{t("model.output")}</dt><dd>{formatMoney(model.cost.output, locale)}</dd></div>
          </dl>
          <CapabilityList model={model} t={t} />
        </div>
        <aside className="estimate-panel">
          <div><span>{t("model.estimateTitle")}</span><strong>{formatMoney(monthlyCost(model, usage.inputMillions, usage.outputMillions, usage.cachePercent), locale)}</strong><p>{t("model.estimateBody", { input: usage.inputMillions, output: usage.outputMillions, cache: usage.cachePercent })}</p></div>
          <CompactUsage usage={usage} t={t} />
        </aside>
      </section>
      <dl className="model-specs">
        <div><dt>{t("model.context")}</dt><dd>{formatTokens(model.context)}</dd></div>
        <div><dt>{t("model.maxOutput")}</dt><dd>{model.maxOutput ? formatTokens(model.maxOutput) : "-"}</dd></div>
        <div><dt>{t("model.released")}</dt><dd>{model.releaseDate || "-"}</dd></div>
        <div><dt>{t("model.updated")}</dt><dd>{model.lastUpdated || "-"}</dd></div>
      </dl>
      <section className="method-section">
        <div><span className="section-icon"><Calculator size={17} /></span><h2>{t("model.formulaTitle")}</h2></div>
        <div className="method-grid">
          <p>{t("model.formulaBody")}</p>
          <p><strong>{t("model.exampleTitle")}</strong> {t("model.exampleBody", {
            inputM: formatTokens(EXAMPLE_INPUT_MILLIONS * 1_000_000),
            outputM: formatTokens(EXAMPLE_OUTPUT_MILLIONS * 1_000_000),
            cachePercent: EXAMPLE_CACHE_PERCENT,
            total: formatMoney(exampleTotal, locale),
            inputRate: formatMoney(model.cost.input, locale),
            // Don't label a fallback input-rate number as this model's "published" cache rate;
            // model.formulaBody already discloses the fallback-to-input-rate behavior in prose.
            cacheRate: model.cost.cacheRead == null ? "-" : formatMoney(model.cost.cacheRead, locale),
            outputRate: formatMoney(model.cost.output, locale),
          })}</p>
          <p><a className="inline-link" href={href(CALCULATOR_PATH, language, { model: model.uid })}>{t("model.calculatorCta")} <ArrowRight size={13} /></a></p>
          {featuredComparisons.length > 0 && (
            <p>
              <strong>{t("model.comparisonsTitle")}</strong>{" "}
              {featuredComparisons.map((item, index) => (
                <span key={item.slug}>
                  {index > 0 && ", "}
                  <a className="inline-link" href={href(`${COMPARE_PATH}/${item.slug}`, language)}>{item.title}</a>
                </span>
              ))}
            </p>
          )}
        </div>
      </section>
      {related.length > 0 && <section className="related-section"><div className="section-title-line"><h2>{t("model.relatedTitle", { provider: model.providerName })}</h2><a href={href(providerPath(model.providerId), language)}>{t("common.viewAll")} <ArrowRight size={14} /></a></div><div className="directory-model-grid">{related.map((item) => <ModelCard key={item.uid} model={item} language={language} locale={locale} t={t} />)}</div></section>}
    </main>
  );
}

function ComparePage({ route, catalog, language, locale, t, usage }: Omit<PageProps, "loading"> & { route: Extract<AppRoute, { type: "compare" }> }) {
  const models = route.modelUids
    .map((uid) => catalog?.models.find((model) => model.uid === uid))
    .filter((model): model is PriceModel => Boolean(model));
  if (models.length < 2) {
    return (
      <main className="subpage"><Breadcrumbs items={[[t("compare.kicker")]]} language={language} t={t} /><div className="route-empty"><BadgeDollarSign size={28} /><h1>{t("compare.emptyTitle")}</h1><p>{t("compare.emptyBody")}</p><a className="primary-button" href={href("/#prices", language)}>{t("compare.start")}</a></div></main>
    );
  }
  const comparisonHref = href("/compare", language, {
    models: models.map((model) => model.uid).join(","),
    input: usage.inputMillions,
    output: usage.outputMillions,
    cache: usage.cachePercent,
  });
  return (
    <main className="subpage">
      <Breadcrumbs items={[[t("compare.kicker")]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy"><span>{t("compare.kicker")}</span><h1>{t("compare.pageTitle")}</h1><p>{t("compare.pageBody")}</p></div>
        <SharePanel title={t("compare.pageTitle")} text={t("compare.pageBody")} url={absoluteUrl(comparisonHref)} itemId={`compare:${models.map((model) => model.uid).join("+")}`} t={t} />
      </header>
      <section className="compare-page-usage"><div><span>{t("compare.usage")}</span><p>{t("model.estimateBody", { input: usage.inputMillions, output: usage.outputMillions, cache: usage.cachePercent })}</p></div><CompactUsage usage={usage} t={t} /></section>
      <CompareModelsGrid models={models} usage={usage} language={language} locale={locale} t={t} />
    </main>
  );
}

function CompareModelsGrid({
  models,
  usage,
  language,
  locale,
  t,
}: {
  models: PriceModel[];
  usage: UsageState;
  language: Language;
  locale: string;
  t: Translator;
}) {
  return (
    <div className="compare-page-grid" style={{ gridTemplateColumns: `150px repeat(${models.length}, minmax(210px, 1fr))` }}>
      <div className="compare-page-label" />
      {models.map((model) => <a className="compare-page-model" href={href(modelPath(model.providerId, model.slug), language)} key={model.uid}><PageLogo model={model} size="lg" /><strong>{model.name}</strong><span>{model.providerName}</span></a>)}
      <div className="compare-page-label">{t("compare.input")}</div>{models.map((model) => <strong key={model.uid}>{formatMoney(model.cost.input, locale)}</strong>)}
      <div className="compare-page-label">{t("compare.cache")}</div>{models.map((model) => <strong key={model.uid}>{model.cost.cacheRead === undefined ? "-" : formatMoney(model.cost.cacheRead, locale)}</strong>)}
      <div className="compare-page-label">{t("compare.output")}</div>{models.map((model) => <strong key={model.uid}>{formatMoney(model.cost.output, locale)}</strong>)}
      <div className="compare-page-label">{t("compare.monthly")}</div>{models.map((model) => <strong className="compare-cost" key={model.uid}>{formatMoney(monthlyCost(model, usage.inputMillions, usage.outputMillions, usage.cachePercent), locale)}</strong>)}
      <div className="compare-page-label">{t("compare.context")}</div>{models.map((model) => <span key={model.uid}>{formatTokens(model.context)}</span>)}
      <div className="compare-page-label">{t("compare.capabilities")}</div>{models.map((model) => <CapabilityList model={model} t={t} />)}
      <div className="compare-page-label">{t("compare.docs")}</div>{models.map((model) => <a className="inline-link" key={model.uid} href={model.providerDoc} target="_blank" rel="noreferrer">{t("compare.viewPrice")} <ExternalLink size={13} /></a>)}
    </div>
  );
}

function GuidePage({ language, t }: Pick<PageProps, "language" | "t">) {
  const guideUrl = absoluteUrl(href(GUIDE_PATH, language));
  return (
    <main className="subpage">
      <Breadcrumbs items={[[t("nav.guide")]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy"><span>{t("guide.kicker")}</span><h1>{t("guide.title")}</h1><p>{t("guide.intro")}</p></div>
        <SharePanel title={t("guide.title")} text={t("guide.intro")} url={guideUrl} itemId="guide:ai-api-pricing" t={t} />
      </header>
      <section className="decision-grid guide-decision-grid">
        {([1, 2, 3] as const).map((number) => {
          const titleKeys = { 1: "guide.inputTitle", 2: "guide.cacheTitle", 3: "guide.fitTitle" } as const;
          const bodyKeys = { 1: "guide.inputBody", 2: "guide.cacheBody", 3: "guide.fitBody" } as const;
          return <article key={number}><span>{String(number).padStart(2, "0")}</span><h2>{t(titleKeys[number])}</h2><p>{t(bodyKeys[number])}</p></article>;
        })}
      </section>
      <section className="method-section guide-method-section" id="method">
        <div><h2>{t("method.title")}</h2></div>
        <div className="method-grid"><p><strong>{t("method.priceTitle")}</strong>{t("method.priceBody")}</p><p><strong>{t("method.sourceTitle")}</strong>{t("method.sourceBefore")} {t("method.sourceAfter")}</p><p><strong>{t("method.costTitle")}</strong>{t("method.costBody")}</p></div>
      </section>
      <section className="faq-section" id="faq" aria-labelledby="faq-title">
        <div className="content-heading"><span>{t("faq.kicker")}</span><h2 id="faq-title">{t("faq.title")}</h2></div>
        <div className="faq-list">{[1, 2, 3, 4, 5].map((number) => <details key={number} open={number === 1}><summary><span>{String(number).padStart(2, "0")}</span>{t(`faq.q${number}` as TranslationKey)}<ChevronDown size={17} /></summary><p>{t(`faq.a${number}` as TranslationKey)}</p></details>)}</div>
      </section>
    </main>
  );
}

function CalculatorPage({ catalog, language, locale, t }: Pick<PageProps, "catalog" | "language" | "locale" | "t">) {
  const copy = requestCostCopy(language);
  const models = catalog?.models ?? fallbackCatalog.models;
  const initial = useState(() => {
    const search = typeof window === "undefined" ? new URLSearchParams() : new URLSearchParams(window.location.search);
    return { input: requestCostInputFromSearch(search), modelUid: search.get("model") };
  })[0];
  const [input, setInput] = useState<RequestCostInput>(initial.input);
  const [modelUid, setModelUid] = useState<string | null>(initial.modelUid);
  const model = resolveCalculatorModel(models, modelUid);
  const debounceRef = useRef<number | undefined>(undefined);
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // An unresolved ?model= (unknown/unavailable uid) must not be silently overwritten with a
    // resolved default here -- that would launder a bad model id back into a valid one on the
    // next reload/share. Leave the URL's `model` param untouched while the unavailable state
    // renders below.
    if (!model) return;
    const target = `${href(CALCULATOR_PATH, language, requestCostSearchParams(input, model?.uid))}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (target !== current) window.history.replaceState(null, "", target);
  }, [input, model?.uid, modelUid, language]);

  useEffect(() => {
    if (typeof window === "undefined" || !model) return undefined;
    // Only explicit form changes count, including under StrictMode effect replay.
    if (!hasInteractedRef.current) return undefined;
    window.clearTimeout(debounceRef.current);
    // Debounced so an analytics event fires once the user pauses editing, not on every keystroke.
    debounceRef.current = window.setTimeout(() => {
      window.gtag?.("event", "calculator_estimate", {
        model_id: model.uid,
        daily_requests: input.dailyRequests,
        billing_days: input.billingDays,
        cache_hit_percent: input.cacheHitPercent,
      });
    }, 800);
    return () => window.clearTimeout(debounceRef.current);
  }, [input, model?.uid]);

  if (!model) {
    return <main className="subpage"><div className="route-empty"><BadgeDollarSign size={28} /><h1>{t("notFound.title")}</h1><p>{t("notFound.body")}</p></div></main>;
  }

  const estimate = estimateRequestCost(model, input);
  const calculatorUrl = absoluteUrl(href(CALCULATOR_PATH, language, requestCostSearchParams(input, model.uid)));
  const numberField = (key: RequestCostField, label: string) => (
    <label key={key}>
      <span>{label}</span>
      <div className="number-input">
        <input
          type="number"
          min={REQUEST_COST_BOUNDS[key].min}
          max={REQUEST_COST_BOUNDS[key].max}
          value={input[key]}
          onChange={(event) => {
            hasInteractedRef.current = true;
            setInput((prev) => ({ ...prev, [key]: clampRequestCostField(key, event.target.value) }));
          }}
        />
      </div>
    </label>
  );

  return (
    <main className="subpage">
      <Breadcrumbs items={[[copy.kicker]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy"><span>{copy.kicker}</span><h1>{copy.title}</h1><p>{copy.intro}</p></div>
        <SharePanel title={copy.title} text={copy.intro} url={calculatorUrl} itemId={`calculator:${model.uid}`} t={t} />
      </header>
      <section className="calculator-section">
        <div className="calculator-grid">
          <div className="usage-controls">
            {numberField("dailyRequests", copy.dailyRequestsLabel)}
            {numberField("inputTokensPerRequest", copy.inputTokensLabel)}
            {numberField("outputTokensPerRequest", copy.outputTokensLabel)}
            {numberField("billingDays", copy.billingDaysLabel)}
            {numberField("cacheHitPercent", copy.cacheHitLabel)}
            <label className="field-full">
              <span>{copy.modelLabel}</span>
              <div className="select-field">
                <select value={model.uid} onChange={(event) => {
                  hasInteractedRef.current = true;
                  setModelUid(event.target.value);
                }}>
                  {models.map((item) => <option key={item.uid} value={item.uid}>{item.providerName} — {item.name}</option>)}
                </select>
                <ChevronDown size={14} />
              </div>
            </label>
          </div>
          <div>
            <dl className="page-stats">
              <div><dt>{copy.totalRequestsLabel}</dt><dd>{formatTokens(estimate.totalRequests)}</dd></div>
              <div><dt>{copy.monthlyEstimateLabel}</dt><dd>{formatMoney(estimate.monthly, locale)}</dd></div>
              <div><dt>{copy.per1000Label}</dt><dd>{formatMoney(estimate.per1000Requests, locale)}</dd></div>
            </dl>
            {model.cost.cacheRead === undefined && input.cacheHitPercent > 0 && (
              <p className="calculator-notice"><Info size={13} /> {copy.noCachePricingNotice}</p>
            )}
            <p><a className="inline-link" href={href(modelPath(model.providerId, model.slug), language)}>{copy.modelLinkLabel} <ArrowRight size={13} /></a></p>
          </div>
        </div>
      </section>
      <section className="method-section">
        <div><span className="section-icon"><Info size={17} /></span><h2>{copy.scopeTitle}</h2></div>
        <div className="method-grid"><p>{copy.scopeBody}</p></div>
      </section>
    </main>
  );
}

function CompareDirectoryPage({ catalog, language, t }: Pick<PageProps, "catalog" | "language" | "t">) {
  const copy = compareDirectoryCopy(language);
  const models = catalog?.models ?? fallbackCatalog.models;
  const directoryUrl = absoluteUrl(href(COMPARE_PATH, language));
  return (
    <main className="subpage">
      <Breadcrumbs items={[[copy.kicker]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy"><span>{copy.kicker}</span><h1>{copy.title}</h1><p>{copy.intro}</p></div>
        <SharePanel title={copy.title} text={copy.intro} url={directoryUrl} itemId="compare:directory" t={t} />
      </header>
      <section className="directory-model-grid">
        {CURATED_COMPARISONS.map((definition, index) => {
          const entryCopy = copy.comparisons[index];
          const resolved = resolveCuratedModels(definition, models);
          if (!resolved.isAvailable) {
            return (
              <div className="directory-model-card is-unavailable" key={definition.slug}>
                <strong>{entryCopy.title}</strong>
                <p>{copy.unavailableNotice}</p>
              </div>
            );
          }
          return (
            <a className="directory-model-card" key={definition.slug} href={href(`${COMPARE_PATH}/${definition.slug}`, language)}>
              <strong>{entryCopy.title}</strong>
              <p>{entryCopy.rationale}</p>
              <dl>
                {resolved.available.map((model) => <div key={model.uid}><dt>{model.providerName}</dt><dd>{model.name}</dd></div>)}
              </dl>
              {resolved.missingCount > 0 && <small>{resolved.missingCount} · {copy.missingModelsNotice}</small>}
            </a>
          );
        })}
      </section>
      <section className="method-section">
        <div><h2>{copy.customTitle}</h2></div>
        <div className="method-grid">
          <p>{copy.customBody}</p>
          <p><a className="primary-button" href={href("/#prices", language)}>{copy.customCta}</a></p>
        </div>
      </section>
    </main>
  );
}

function CuratedComparePage({
  route,
  catalog,
  language,
  locale,
  t,
  usage,
}: Omit<PageProps, "loading"> & { route: Extract<AppRoute, { type: "curated-compare" }> }) {
  const definition = findCuratedComparison(route.slug);
  const copy = compareDirectoryCopy(language);
  const index = CURATED_COMPARISONS.findIndex((item) => item.slug === route.slug);
  useEffect(() => {
    if (!definition) return;
    window.gtag?.("event", "compare_curated_view", { comparison: definition.slug });
  }, [definition?.slug]);
  if (!definition || index === -1) {
    return <NotFoundPage language={language} t={t} />;
  }
  const entryCopy = copy.comparisons[index];
  const models = catalog?.models ?? fallbackCatalog.models;
  const resolved = resolveCuratedModels(definition, models);
  const curatedUrl = absoluteUrl(href(`${COMPARE_PATH}/${definition.slug}`, language, {
    input: usage.inputMillions,
    output: usage.outputMillions,
    cache: usage.cachePercent,
  }));
  const costs = resolved.available.map((model) => monthlyCost(model, usage.inputMillions, usage.outputMillions, usage.cachePercent));
  const costDifference = costs.length > 1 ? Math.max(...costs) - Math.min(...costs) : 0;

  return (
    <main className="subpage">
      <Breadcrumbs items={[[copy.kicker, COMPARE_PATH], [entryCopy.title]]} language={language} t={t} />
      <header className="page-hero">
        <div className="page-hero-copy"><span>{copy.kicker}</span><h1>{entryCopy.title}</h1><p>{entryCopy.rationale}</p></div>
        <SharePanel title={entryCopy.title} text={entryCopy.rationale} url={curatedUrl} itemId={`compare:${definition.slug}`} t={t} />
      </header>
      {!resolved.isAvailable ? (
        <div className="route-empty"><BadgeDollarSign size={28} /><h1>{entryCopy.title}</h1><p>{copy.unavailableNotice}</p><a className="primary-button" href={href(COMPARE_PATH, language)}>{t("common.viewAll")}</a></div>
      ) : (
        <>
          <section className="compare-page-usage"><div><span>{entryCopy.workloadNote}</span><p>{t("model.estimateBody", { input: usage.inputMillions, output: usage.outputMillions, cache: usage.cachePercent })}</p></div><CompactUsage usage={usage} t={t} /></section>
          {resolved.missingCount > 0 && <p className="calculator-notice"><Info size={13} /> {resolved.missingCount} · {copy.missingModelsNotice}</p>}
          <p className="curated-cost-difference"><strong>{copy.costDifferenceLabel}:</strong> {formatMoney(costDifference, locale)}</p>
          <CompareModelsGrid models={resolved.available} usage={usage} language={language} locale={locale} t={t} />
          <section className="method-section">
            <div><h2>{copy.limitationsTitle}</h2></div>
            <div className="method-grid"><p>{t("method.priceBody")}</p><p>{t("method.costBody")}</p><p>{copy.versionNotice}</p></div>
          </section>
        </>
      )}
    </main>
  );
}

function NotFoundPage({ language, t }: Pick<PageProps, "language" | "t">) {
  return <main className="subpage"><div className="route-empty"><BadgeDollarSign size={28} /><h1>{t("notFound.title")}</h1><p>{t("notFound.body")}</p><a className="primary-button" href={href("/", language)}>{t("common.backPricing")}</a></div></main>;
}

export function SubpageRouter(props: PageProps) {
  const { route, catalog, loading, language, t } = props;
  if (loading && route.type !== "guide") return <main className="subpage"><div className="page-loading"><span /><span /><span /></div></main>;
  if (route.type === "rankings") return <RankingsPage catalog={catalog ?? fallbackCatalog} language={language} />;
  if (route.type === "providers") return <ProvidersPage {...props} />;
  if (route.type === "provider") return <ProviderPage {...props} route={route} />;
  if (route.type === "model") return <ModelPage {...props} route={route} />;
  if (route.type === "compare") return <ComparePage {...props} route={route} />;
  if (route.type === "compare-directory") return <CompareDirectoryPage {...props} />;
  if (route.type === "curated-compare") return <CuratedComparePage {...props} route={route} />;
  if (route.type === "calculator") return <CalculatorPage {...props} />;
  if (route.type === "guide") return <GuidePage language={language} t={t} />;
  return <NotFoundPage language={language} t={t} />;
}

export function HomeDiscovery({ language, t }: { language: Language; t: Translator }) {
  const items = [
    [rankingsCopy(language)[0], rankingsCopy(language)[1], "/rankings"],
    [t("home.calculatorTitle"), t("home.calculatorBody"), CALCULATOR_PATH],
    [t("home.compareDirectoryTitle"), t("home.compareDirectoryBody"), COMPARE_PATH],
    [t("home.providersTitle"), t("home.providersBody"), providerPath()],
    [t("home.guideTitle"), t("home.guideBody"), GUIDE_PATH],
  ];
  return (
    <section className="home-discovery">
      <div className="content-heading"><span>{t("home.exploreKicker")}</span><h2>{t("home.exploreTitle")}</h2></div>
      <div>{items.map(([title, body, path]) => <a key={path} href={href(path, language)}><span><strong>{title}</strong><p>{body}</p></span><span>{t("home.open")} <ArrowRight size={15} /></span></a>)}</div>
    </section>
  );
}
